// AccessibilityPanel — reusable controls extracted from Profile.tsx (7cc6bb4)
// per the relocation task. Same provider, same localStorage shape, same
// behaviors — only the host surface changes.
//
// Used by:
//   - `pages/Settings.tsx` (top-level Settings route via sidebar nav)
//   - `components/layout/Layout.tsx` header popover (quick-access dropdown)
//
// The panel is purely presentational over `useAccessibility()` — no local
// state, no side effects beyond what the provider already runs.

import React from 'react';
import { Type, Contrast, Zap, RotateCcw } from 'lucide-react';
import { useAccessibility, AccessibilityFontSize } from '../../contexts/AccessibilityContext';

const FONT_SIZE_OPTIONS: Array<{ value: AccessibilityFontSize; label: string; sample: string }> = [
    { value: 'small', label: 'Small', sample: 'Aa' },
    { value: 'medium', label: 'Medium', sample: 'Aa' },
    { value: 'large', label: 'Large', sample: 'Aa' },
    { value: 'xlarge', label: 'XLarge', sample: 'Aa' },
];

interface ToggleRowProps {
    icon: React.ReactNode;
    label: string;
    description: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ icon, label, description, checked, onChange }) => (
    <div className="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
        <div className="flex items-start gap-2 min-w-0 flex-1">
            <div className="mt-0.5 flex-shrink-0">{icon}</div>
            <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            </div>
        </div>
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    checked ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    </div>
);

const AccessibilityPanel: React.FC = () => {
    const { prefs, setPref, reset } = useAccessibility();

    return (
        <div className="space-y-6">
            {/* Font size scale */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <Type className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Font size</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {FONT_SIZE_OPTIONS.map(opt => {
                        const selected = prefs.fontSize === opt.value;
                        // Each preview swatch shows the relative scale so the user can
                        // pick by visual size, not just the label.
                        const previewSize =
                            opt.value === 'small' ? 'text-base' :
                            opt.value === 'medium' ? 'text-lg' :
                            opt.value === 'large' ? 'text-xl' : 'text-2xl';
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setPref('fontSize', opt.value)}
                                aria-pressed={selected}
                                className={`flex flex-col items-center justify-center py-3 rounded-xl border-2 transition-all ${
                                    selected
                                        ? 'border-primary bg-blue-50 dark:bg-blue-900/20 text-primary'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                <span className={`font-bold ${previewSize}`}>{opt.sample}</span>
                                <span className="text-xs mt-1">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Toggle: high contrast */}
            <ToggleRow
                icon={<Contrast className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                label="High contrast"
                description="Boost color contrast for body text, surfaces, borders, and focus rings."
                checked={prefs.highContrast}
                onChange={v => setPref('highContrast', v)}
            />

            {/* Toggle: reduce motion */}
            <ToggleRow
                icon={<Zap className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                label="Reduce motion"
                description="Disable animations and transitions site-wide."
                checked={prefs.reduceMotion}
                onChange={v => setPref('reduceMotion', v)}
            />

            {/* Toggle: dyslexic font */}
            <ToggleRow
                icon={<Type className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                label="Dyslexic-friendly font"
                description="Switch the body font to OpenDyslexic for easier reading."
                checked={prefs.dyslexicFont}
                onChange={v => setPref('dyslexicFont', v)}
            />

            {/* Reset */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                <button
                    type="button"
                    onClick={reset}
                    className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                    <RotateCcw className="w-4 h-4" />
                    Reset to defaults
                </button>
            </div>
        </div>
    );
};

export default AccessibilityPanel;
